import type { RosterMember } from '../../api/manager-team'
import { displayName } from '../../lib/display-name'
import { fmtDateRange } from '../../lib/format'
import {
  safeLeaveStyle,
  ROSTER_GRID_TEMPLATE,
  ROSTER_GRID_GAP,
  ROSTER_ROW_PAD_X,
} from '../../lib/roster-format'
import { Card } from '../primitives/Card'
import { RhythmAvatar } from './RhythmAvatar'

interface RosterRowProps {
  member: RosterMember
  onOpen: (member: RosterMember) => void
  /** True when this row is the signed-in viewer — surfaces a "You" chip. */
  isSelf?: boolean
}

/**
 * A single row on the manager team roster, a two-column grid
 * (Person | Next leave) sharing ROSTER_GRID_TEMPLATE with the column header so
 * values align down each column. The person cell leads with a RhythmAvatar
 * (coloured by leave-rhythm band, band glyph in the centre) instead of a
 * wellness number. Full balances + history live in the detail modal that
 * opens on row click.
 *
 * Accessibility: a stretched, transparent overlay <button> fills the row and
 * carries the click/keyboard affordance for opening the modal; the inner cells
 * sit above it with `pointerEvents: 'none'` so the whole row is one target.
 */
export function RosterRow({ member, onOpen, isSelf = false }: RosterRowProps): JSX.Element {
  const name = displayName(member)
  // Team lives in its own column now (not a subtitle, and not the job title —
  // position is surfaced only in the org chart). Fall back to the parent
  // department, then an em dash when neither is set.
  const teamText = member.teamName ?? member.departmentName ?? '—'

  const nextLeave = member.nextLeave
  const nextLeaveText = nextLeave
    ? `${fmtDateRange(nextLeave.startDate, nextLeave.endDate)} · ${safeLeaveStyle(nextLeave.leaveType).short}`
    : '—'

  return (
    <Card
      data-testid="roster-row"
      data-member-id={member.id}
      style={{
        background: 'var(--surface)',
        minHeight: 56,
        display: 'grid',
        gridTemplateColumns: ROSTER_GRID_TEMPLATE,
        alignItems: 'center',
        columnGap: ROSTER_GRID_GAP,
        padding: `10px ${ROSTER_ROW_PAD_X}px`,
        position: 'relative',
      }}
    >
      {/* Stretched overlay button — the row's click + keyboard affordance. */}
      <button
        type="button"
        data-testid="roster-row-open"
        aria-label={`View ${name}`}
        onClick={() => onOpen(member)}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          margin: 0,
          padding: 0,
          borderRadius: 12,
        }}
      />

      {/* Column 1 — Person: rhythm avatar + identity */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          minWidth: 0,
          position: 'relative',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      >
        <RhythmAvatar
          name={name}
          band={member.health.band}
          patternCount={member.health.contributingPatterns.length}
          size={36}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span
            style={{
              fontWeight: 600,
              fontSize: 14,
              color: 'var(--text)',
              minWidth: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {name}
          </span>
          {isSelf ? (
            <span
              data-testid="roster-you-chip"
              style={{
                flexShrink: 0,
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: '0.02em',
                color: 'var(--steel-blue)',
                background: 'var(--baby-blue-tint)',
                border: '1px solid var(--steel-blue)',
                borderRadius: 999,
                padding: '1px 6px',
                lineHeight: 1.4,
              }}
            >
              You
            </span>
          ) : null}
        </div>
      </div>

      {/* Column 2 — Team */}
      <div
        style={{
          fontSize: 13,
          color: 'var(--text-2)',
          minWidth: 0,
          position: 'relative',
          zIndex: 1,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={teamText}
      >
        {teamText}
      </div>

      {/* Column 3 — Next leave */}
      <div
        style={{
          fontSize: 12,
          color: 'var(--text-2)',
          minWidth: 0,
          position: 'relative',
          zIndex: 1,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={nextLeave ? nextLeaveText : undefined}
      >
        {nextLeaveText}
      </div>
    </Card>
  )
}
